import React from "react";
import classNames from "classnames";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  variant?: "primary" | "secondary" | "outline" | "transparent" | 'tertiary';
  size?: "sm" | "md" | "lg" | "mdm";
  loading?: boolean;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  children,
  type = "button",
  variant = "primary",
  size = "mdm",
  loading = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className,
  disabled,
  ...props
}) => {
  const baseStyles =
    "inline-flex items-center justify-center font-medium rounded-xl transition-all duration-200 focus:outline-none disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed";

  const variants = {
    primary:
      "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg hover:shadow-xl active:scale-[0.98]",
    secondary:
      "bg-white/10 backdrop-blur-sm border border-white/20 text-white hover:bg-white/20 hover:border-white/30 ",
    outline:
      "border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 ",
    transparent:
      "  hover:bg-[linear-gradient(270deg,var(--brand-gradient-from)_0%,var(--brand-gradient-to)_98.3%)] hover:border-white/50 ",
    tertiary:
    "bg-[#171820] backdrop-blur-sm border border-[#171820] text-white hover:bg-white/20 hover:border-white/30 ",
  };

  const sizes = {
    sm: "h-10 px-4 text-sm gap-2",
    md: "h-11 px-6 text-base gap-3",
    mdm: "h-12 px-6 text-[16px] gap-3",
    lg: "h-12 px-8 text-lg gap-3",
  };

  const widthStyles = fullWidth ? "w-full" : "";

  return (
    <button
      className={classNames(
        baseStyles,
        variants[variant],
        sizes[size],
        widthStyles,
        className
      )}
      type={type}
      disabled={disabled || loading}
      {...props}
    >
      {loading ? (
        <>
          <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
         
        </>
      ) : (
        <>
          {leftIcon && <span className="shrink-0">{leftIcon}</span>}
          <span>{children}</span>
          {rightIcon && <span className="shrink-0">{rightIcon}</span>}
        </>
      )}
    </button>
  );
};

export default Button;
